"use strict";
/**
 * Text validation rules and utilities
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LEVEL_TEXT_SIZE_RULES = exports.TEXT_VALIDATION_RULES = void 0;
exports.validateTextSizeForLevel = validateTextSizeForLevel;
exports.estimateReadingTime = estimateReadingTime;
exports.validateText = validateText;
exports.isValidTextSizeForLevel = isValidTextSizeForLevel;
exports.getRecommendedTextSizesForLevel = getRecommendedTextSizesForLevel;
const textSizes_1 = require("../constants/textSizes");
const textTokenizer_1 = require("../utils/textTokenizer");
/**
 * Minimum text requirements
 */
exports.TEXT_VALIDATION_RULES = {
    MIN_CONTENT_LENGTH: 10,
    MAX_CONTENT_LENGTH: 5000,
    MIN_WORD_COUNT: 5,
    MAX_TOPIC_LENGTH: 200,
    // Language-specific minimums
    MIN_WORDS_CJK: 10,
    MIN_WORDS_LATIN: 20,
};
/**
 * Text size validation rules by level
 */
exports.LEVEL_TEXT_SIZE_RULES = {
    BEGINNER: ["SHORT"],
    ELEMENTARY: ["SHORT"],
    INTERMEDIATE: ["SHORT", "MEDIUM"],
    UPPER_INTERMEDIATE: ["SHORT", "MEDIUM"],
    ADVANCED: ["SHORT", "MEDIUM", "LONG"],
    PROFICIENT: ["SHORT", "MEDIUM", "LONG"],
};
/**
 * Validate text size for a specific level
 */
function validateTextSizeForLevel(level, textSize) {
    const allowedSizes = exports.LEVEL_TEXT_SIZE_RULES[level];
    const isValid = allowedSizes.includes(textSize);
    return {
        isValid,
        allowedSizes,
        reason: isValid
            ? undefined
            : `Text size ${textSize} is not allowed for level ${level}`,
    };
}
/**
 * Calculate reading time estimate (in seconds)
 */
function estimateReadingTime(wordCount, language) {
    // Reading speeds (words per minute) by language type
    const WPM_RATES = {
        cjk: 200, // Chinese, Japanese, Korean
        latin: 250, // English, Spanish, French, etc.
        default: 225,
    };
    let wpm;
    if (language && ["zh", "ja", "ko"].includes(language)) {
        wpm = WPM_RATES.cjk;
    }
    else if (language && ["en", "es", "fr", "de", "it"].includes(language)) {
        wpm = WPM_RATES.latin;
    }
    else {
        wpm = WPM_RATES.default;
    }
    return Math.ceil((wordCount / wpm) * 60);
}
/**
 * Comprehensive text validation
 */
function validateText(content, level, textSize, language) {
    const errors = [];
    const warnings = [];
    // Basic content validation
    if (!content || content.trim().length === 0) {
        errors.push("Content cannot be empty");
    }
    if (content.length < exports.TEXT_VALIDATION_RULES.MIN_CONTENT_LENGTH) {
        errors.push(`Content too short (minimum ${exports.TEXT_VALIDATION_RULES.MIN_CONTENT_LENGTH} characters)`);
    }
    if (content.length > exports.TEXT_VALIDATION_RULES.MAX_CONTENT_LENGTH) {
        errors.push(`Content too long (maximum ${exports.TEXT_VALIDATION_RULES.MAX_CONTENT_LENGTH} characters)`);
    }
    // Word count validation
    const wordCount = (0, textTokenizer_1.countWords)(content, language);
    const sizeRange = (0, textSizes_1.getTextLengthRange)(textSize, language);
    const minWords = sizeRange.unit === "characters"
        ? exports.TEXT_VALIDATION_RULES.MIN_WORDS_CJK
        : exports.TEXT_VALIDATION_RULES.MIN_WORDS_LATIN;
    if (wordCount < minWords) {
        errors.push(`Too few words (minimum ${minWords} for ${language || "this language"})`);
    }
    // Text size validation
    const length = sizeRange.unit === "characters" ? [...content].length : wordCount;
    if (length < sizeRange.min) {
        warnings.push(`Text length (${length}) is below expected range for ${textSize} texts (${sizeRange.min}-${sizeRange.max} ${sizeRange.unit})`);
    }
    else if (length > sizeRange.max) {
        warnings.push(`Text length (${length}) exceeds expected range for ${textSize} texts (${sizeRange.min}-${sizeRange.max} ${sizeRange.unit})`);
    }
    // Level appropriateness
    const sizeValidation = validateTextSizeForLevel(level, textSize);
    if (!sizeValidation.isValid) {
        errors.push(sizeValidation.reason);
    }
    // Calculate statistics
    const sentences = (0, textTokenizer_1.splitIntoSentences)(content, language);
    const sentenceCount = sentences.length;
    const avgWordsPerSentence = sentenceCount > 0 ? wordCount / sentenceCount : 0;
    const estimatedReadingTime = estimateReadingTime(wordCount, language);
    // Sentence length warnings
    if (avgWordsPerSentence < 3) {
        warnings.push("Sentences are very short on average");
    }
    else if (avgWordsPerSentence > 20) {
        warnings.push("Sentences are very long on average");
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        stats: {
            wordCount,
            sentenceCount,
            avgWordsPerSentence,
            estimatedReadingTime,
        },
    };
}
/**
 * Quick validation for text size compatibility
 */
function isValidTextSizeForLevel(level, textSize) {
    return exports.LEVEL_TEXT_SIZE_RULES[level].includes(textSize);
}
/**
 * Get recommended text sizes for a level
 */
function getRecommendedTextSizesForLevel(level) {
    return [...exports.LEVEL_TEXT_SIZE_RULES[level]];
}
//# sourceMappingURL=textValidation.js.map